import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  createAdminEvent,
  deleteAdminEvent,
  exportMembersCsv,
  exportMembersPdf,
  getAlumniContent,
  getAdminEvents,
  getAdminMemberProfile,
  getAdminView,
  getDefaultAlumniContent,
  getDefaultMemberProfileSchema,
  getMemberProfileSchema,
  getSiteContent,
  saveAdminMemberProfile,
  updateAlumniContent,
  updateAdminEvent,
  updateSiteContent,
  uploadAlumniBanner,
  setMemberStatus
} from '../services/authService';
import './Dashboard.css';

const EMPTY_EVENT_FORM = {
  title: '',
  dateLabel: '',
  description: '',
  action: '',
  actionUrl: '',
  sortOrder: 0,
  active: true
};

function toMonthInputValue(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const isoMatch = raw.match(/^(\d{4})-(0[1-9]|1[0-2])$/);
  if (isoMatch) {
    return `${isoMatch[1]}-${isoMatch[2]}`;
  }

  const slashMatch = raw.match(/^(0[1-9]|1[0-2])\/(\d{4})$/);
  if (slashMatch) {
    return `${slashMatch[2]}-${slashMatch[1]}`;
  }

  const dateMatch = raw.match(/^(\d{4})-(0[1-9]|1[0-2])-\d{2}$/);
  if (dateMatch) {
    return `${dateMatch[1]}-${dateMatch[2]}`;
  }

  return raw;
}

function renderField(field, value, onChange) {
  const commonProps = {
    name: field.name,
    required: field.required,
    placeholder: field.placeholder || ''
  };

  if (field.type === 'textarea') {
    return (
      <textarea
        {...commonProps}
        value={value || ''}
        onChange={(event) => onChange(field.name, event.target.value)}
        rows={4}
      />
    );
  }

  if (field.type === 'select') {
    return (
      <select
        {...commonProps}
        value={value || ''}
        onChange={(event) => onChange(field.name, event.target.value)}
      >
        <option value="">Select {field.label}</option>
        {field.options.map((option) => (
          <option key={`${field.name}-${option.value}`} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  if (field.type === 'file') {
    return (
      <input
        {...commonProps}
        type="file"
        accept={field.accept || 'image/*'}
        onChange={(event) => onChange(field.name, event.target.files?.[0] || null)}
      />
    );
  }

  if (field.type === 'month') {
    const monthValue = toMonthInputValue(value);

    return (
      <input
        {...commonProps}
        type="month"
        value={monthValue}
        placeholder="YYYY-MM"
        pattern="\d{4}-(0[1-9]|1[0-2])"
        title="Use YYYY-MM"
        onChange={(event) => onChange(field.name, event.target.value)}
      />
    );
  }

  return (
    <input
      {...commonProps}
      type={field.type || 'text'}
      value={value || ''}
      onChange={(event) => onChange(field.name, event.target.value)}
    />
  );
}

export default function AdminDashboard() {
  const { logout } = useAuth();
  const [activeTab, setActiveTab] = useState('members');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [schema, setSchema] = useState(getDefaultMemberProfileSchema());
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventSaving, setEventSaving] = useState(false);
  const [eventDeletingId, setEventDeletingId] = useState('');
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [editingEventId, setEditingEventId] = useState('');
  const [eventForm, setEventForm] = useState(EMPTY_EVENT_FORM);
  const [siteForm, setSiteForm] = useState({
    companyName: '',
    brandTitle: '',
    brandSubtitle: '',
    topHeaderAddress: '',
    topHeaderEmail: '',
    topHeaderPhone: '',
    logoUrl: '',
    linkedinUrl: '',
    facebookUrl: '',
    instagramUrl: '',
    whatsappUrl: '',
    logoFile: null,
    removeLogo: false
  });
  const [alumniForm, setAlumniForm] = useState(getDefaultAlumniContent());
  const [memberSearch, setMemberSearch] = useState('');
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [bannerUploadingIndex, setBannerUploadingIndex] = useState(-1);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const loadEvents = async () => {
    setEventsLoading(true);
    try {
      const list = await getAdminEvents();
      setEvents(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err.message || 'Unable to load events.');
    } finally {
      setEventsLoading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [adminData, fields] = await Promise.all([
        getAdminView(),
        getMemberProfileSchema()
      ]);
      setMembers(adminData.members || []);
      setSchema(fields);

      try {
        const siteSettings = await getSiteContent();
        setSiteForm((prev) => ({ ...prev, ...siteSettings, logoFile: null, removeLogo: false }));
      } catch (siteError) {
        // Keep member management usable even if site settings fails.
      }

      try {
        const content = await getAlumniContent();
        setAlumniForm(content);
      } catch (contentError) {
        // Keep member management usable even if alumni content fails.
      }

      try {
        await loadEvents();
      } catch (eventsError) {
        // Keep core admin flow available even if events endpoint fails.
      }
    } catch (err) {
      setError(err.message || 'Unable to load admin dashboard.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredMembers = useMemo(() => {
    const q = memberSearch.trim().toLowerCase();
    if (!q) return members;
    return members.filter((member) =>
      [
        member.fullName,
        member.email,
        member.designation,
        member.current_organization,
        member.ca_status,
        member.icab_registration_no
      ]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [memberSearch, members]);

  const openMemberProfile = async (memberId) => {
    setSelectedMemberId(memberId);
    setProfileModalOpen(true);
    setProfileLoading(true);
    setError('');
    setInfo('');
    try {
      const profile = await getAdminMemberProfile(memberId);
      setSelectedProfile(profile);
    } catch (err) {
      setError(err.message || 'Unable to load member profile.');
    } finally {
      setProfileLoading(false);
    }
  };

  const closeProfileModal = () => {
    setProfileModalOpen(false);
    setSelectedMemberId('');
    setSelectedProfile(null);
    setProfileLoading(false);
    setProfileSaving(false);
  };

  const handleProfileFieldChange = (name, value) => {
    setSelectedProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleStatusToggle = async (member) => {
    setError('');
    setInfo('');
    try {
      await setMemberStatus(member.id, !member.active);
      setInfo(`${member.fullName} ${member.active ? 'deactivated' : 'activated'} successfully.`);
      await load();
    } catch (err) {
      setError(err.message || 'Status change failed.');
    }
  };

  const handleSaveProfile = async (event) => {
    event.preventDefault();
    if (!selectedMemberId || !selectedProfile) return;

    setProfileSaving(true);
    setError('');
    setInfo('');
    try {
      await saveAdminMemberProfile(selectedMemberId, selectedProfile, schema);
      const freshProfile = await getAdminMemberProfile(selectedMemberId);
      setSelectedProfile(freshProfile);
      setInfo('Member profile updated successfully.');
      await load();
    } catch (err) {
      setError(err.message || 'Could not update member profile.');
    } finally {
      setProfileSaving(false);
    }
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    setSidebarOpen(false);
  };

  const setSiteField = (name, value) => {
    setSiteForm((prev) => ({ ...prev, [name]: value }));
  };

  const setAlumniField = (name, value) => {
    setAlumniForm((prev) => ({ ...prev, [name]: value }));
  };

  const setAlumniBannerField = (index, name, value) => {
    setAlumniForm((prev) => {
      const list = Array.isArray(prev.banners) ? [...prev.banners] : [];
      const existing = list[index] || { imageUrl: '', heading: '' };
      list[index] = { ...existing, [name]: value };
      return { ...prev, banners: list };
    });
  };

  const addAlumniBanner = () => {
    setAlumniForm((prev) => ({
      ...prev,
      banners: [...(Array.isArray(prev.banners) ? prev.banners : []), { imageUrl: '', heading: '' }]
    }));
  };

  const removeAlumniBanner = (index) => {
    setAlumniForm((prev) => ({
      ...prev,
      banners: (Array.isArray(prev.banners) ? prev.banners : []).filter((_, i) => i !== index)
    }));
  };

  const handleAlumniBannerUpload = async (index, file) => {
    if (!file) return;
    setBannerUploadingIndex(index);
    setError('');
    setInfo('');
    try {
      const res = await uploadAlumniBanner(file);
      const nextBanners = [...(Array.isArray(alumniForm.banners) ? alumniForm.banners : [])];
      const existing = nextBanners[index] || { imageUrl: '', heading: '' };
      nextBanners[index] = { ...existing, imageUrl: res.imageUrl || '' };
      const nextContent = {
        ...alumniForm,
        banners: nextBanners
      };
      setAlumniForm(nextContent);

      const saved = await updateAlumniContent(nextContent);
      setAlumniForm(saved.content || nextContent);
      setInfo('Banner uploaded and saved successfully.');
    } catch (err) {
      setError(err.message || 'Banner upload failed.');
    } finally {
      setBannerUploadingIndex(-1);
    }
  };

  const setEventField = (name, value) => {
    setEventForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetEventEditor = () => {
    setEditingEventId('');
    setEventForm(EMPTY_EVENT_FORM);
  };

  const openAddEventModal = () => {
    resetEventEditor();
    setEventModalOpen(true);
  };

  const closeEventModal = () => {
    setEventModalOpen(false);
    resetEventEditor();
    setEventSaving(false);
  };

  const startEditEvent = (event) => {
    setEditingEventId(String(event.id || ''));
    setEventForm({
      title: event.title || '',
      dateLabel: event.dateLabel || '',
      description: event.description || '',
      action: event.action || '',
      actionUrl: event.actionUrl || '',
      sortOrder: Number.isFinite(Number(event.sortOrder)) ? Number(event.sortOrder) : 0,
      active: !!event.active
    });
    setActiveTab('events');
    setSidebarOpen(false);
    setEventModalOpen(true);
  };

  const handleSaveEvent = async (event) => {
    event.preventDefault();
    if (!eventForm.title.trim()) {
      setError('Event title is required.');
      return;
    }

    setError('');
    setInfo('');
    setEventSaving(true);

    try {
      const payload = {
        ...eventForm,
        title: eventForm.title.trim()
      };

      if (editingEventId) {
        await updateAdminEvent(editingEventId, payload);
        setInfo('Event updated successfully.');
      } else {
        await createAdminEvent(payload);
        setInfo('Event created successfully.');
      }

      setEventModalOpen(false);
      resetEventEditor();
      await loadEvents();
    } catch (err) {
      setError(err.message || 'Could not save event.');
    } finally {
      setEventSaving(false);
    }
  };

  const handleDeleteEvent = async (event) => {
    if (!event?.id) return;

    const confirmed = window.confirm(`Delete "${event.title}"?`);
    if (!confirmed) return;

    setError('');
    setInfo('');
    setEventDeletingId(String(event.id));
    try {
      await deleteAdminEvent(event.id);
      setInfo('Event deleted successfully.');
      if (editingEventId && String(event.id) === editingEventId) {
        setEventModalOpen(false);
        resetEventEditor();
      }
      await loadEvents();
    } catch (err) {
      setError(err.message || 'Could not delete event.');
    } finally {
      setEventDeletingId('');
    }
  };

  const handleSaveSiteSettings = async (event) => {
    event.preventDefault();
    setError('');
    setInfo('');
    try {
      const res = await updateSiteContent(siteForm);
      setSiteForm((prev) => ({
        ...prev,
        ...(res.settings || {}),
        logoFile: null,
        removeLogo: false
      }));
      setInfo(res.message || 'Site settings saved successfully.');
    } catch (err) {
      setError(err.message || 'Could not save site settings.');
    }
  };

  const handleSaveAlumniContent = async (event) => {
    event.preventDefault();
    setError('');
    setInfo('');
    try {
      const res = await updateAlumniContent(alumniForm);
      setAlumniForm(res.content || alumniForm);
      setInfo(res.message || 'Alumni content saved successfully.');
    } catch (err) {
      setError(err.message || 'Could not save alumni content.');
    }
  };

  return (
    <div className="dashboard-shell">
      <header className="dashboard-shell-header">
        <h1>Admin dashboard</h1>
        <div className="dashboard-shell-actions">
          <button
            type="button"
            className="sidebar-toggle"
            onClick={() => setSidebarOpen((prev) => !prev)}
            aria-expanded={sidebarOpen}
            aria-controls="admin-dashboard-sidebar"
          >
            Menu
          </button>
          <button type="button" onClick={() => exportMembersCsv(members)}>
            Export to Excel (CSV)
          </button>
          <button type="button" onClick={() => exportMembersPdf(members)}>
            Download members (PDF)
          </button>
          <Link to="/">Public site</Link>
          <button type="button" onClick={logout}>Logout</button>
        </div>
      </header>

      {error && <p className="error-text">{error}</p>}
      {info && <p className="success-text">{info}</p>}

      <div className="admin-layout">
        <aside
          id="admin-dashboard-sidebar"
          className={`admin-sidebar dashboard-sidebar${sidebarOpen ? ' open' : ''}`}
        >
          <h3>Admin Menu</h3>
          <button
            type="button"
            className={activeTab === 'members' ? 'active' : ''}
            onClick={() => switchTab('members')}
          >
            Members
          </button>
          <button
            type="button"
            className={activeTab === 'site' ? 'active' : ''}
            onClick={() => switchTab('site')}
          >
            Site Settings
          </button>
          <button
            type="button"
            className={activeTab === 'alumni' ? 'active' : ''}
            onClick={() => switchTab('alumni')}
          >
            Alumni Content
          </button>
          <button
            type="button"
            className={activeTab === 'events' ? 'active' : ''}
            onClick={() => switchTab('events')}
          >
            Events
          </button>
        </aside>

        <div className="admin-main">
          {activeTab === 'members' && (
            <>
              <section className="panel">
                <h2>Member management</h2>
                <p>Review members, update profiles, and manage account access.</p>
                <div className="panel-filters">
                  <input
                    type="text"
                    placeholder="Search by name, email, designation, organization..."
                    value={memberSearch}
                    onChange={(event) => setMemberSearch(event.target.value)}
                  />
                </div>

                {loading ? (
                  <p>Loading members...</p>
                ) : (
                  <>
                    <p>Showing {filteredMembers.length} of {members.length} members.</p>
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Designation</th>
                            <th>CA Status</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredMembers.map((member) => (
                            <tr key={member.id}>
                              <td>{member.fullName}</td>
                              <td>{member.email}</td>
                              <td>{member.designation || '-'}</td>
                              <td>{member.ca_status || '-'}</td>
                              <td>{member.active ? 'Active' : 'Inactive'}</td>
                              <td className="table-actions">
                                <button type="button" onClick={() => openMemberProfile(member.id)}>
                                  Edit profile
                                </button>
                                <button type="button" onClick={() => handleStatusToggle(member)}>
                                  {member.active ? 'Deactivate' : 'Activate'}
                                </button>
                              </td>
                            </tr>
                          ))}
                          {!filteredMembers.length && (
                            <tr>
                              <td colSpan="6">No members found.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </section>
            </>
          )}

          {activeTab === 'site' && (
            <section className="panel">
              <h2>Site settings</h2>
              <p>Update branding, top header, logo, and social links shown across the public site.</p>
              <form className="panel-grid" onSubmit={handleSaveSiteSettings}>
                <label>
                  Company name
                  <input
                    type="text"
                    value={siteForm.companyName}
                    onChange={(event) => setSiteField('companyName', event.target.value)}
                  />
                </label>
                <label>
                  Brand title
                  <input
                    type="text"
                    value={siteForm.brandTitle}
                    onChange={(event) => setSiteField('brandTitle', event.target.value)}
                  />
                </label>
                <label>
                  Brand subtitle
                  <input
                    type="text"
                    value={siteForm.brandSubtitle}
                    onChange={(event) => setSiteField('brandSubtitle', event.target.value)}
                  />
                </label>
                <label>
                  Top header address
                  <input
                    type="text"
                    value={siteForm.topHeaderAddress}
                    onChange={(event) => setSiteField('topHeaderAddress', event.target.value)}
                  />
                </label>
                <label>
                  Top header email
                  <input
                    type="email"
                    value={siteForm.topHeaderEmail}
                    onChange={(event) => setSiteField('topHeaderEmail', event.target.value)}
                  />
                </label>
                <label>
                  Top header phone
                  <input
                    type="text"
                    value={siteForm.topHeaderPhone}
                    onChange={(event) => setSiteField('topHeaderPhone', event.target.value)}
                  />
                </label>
                <label className="field-span-full">
                  Current logo URL
                  <input
                    type="url"
                    value={siteForm.logoUrl}
                    readOnly
                  />
                </label>
                <label className="field-span-full">
                  Upload new logo
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(event) => setSiteField('logoFile', event.target.files?.[0] || null)}
                  />
                </label>
                <label className="inline-check field-span-full">
                  <input
                    type="checkbox"
                    checked={siteForm.removeLogo}
                    onChange={(event) => setSiteField('removeLogo', event.target.checked)}
                  />
                  Remove current logo
                </label>
                <label>
                  LinkedIn URL
                  <input
                    type="url"
                    value={siteForm.linkedinUrl}
                    onChange={(event) => setSiteField('linkedinUrl', event.target.value)}
                  />
                </label>
                <label>
                  Facebook URL
                  <input
                    type="url"
                    value={siteForm.facebookUrl}
                    onChange={(event) => setSiteField('facebookUrl', event.target.value)}
                  />
                </label>
                <label>
                  Instagram URL
                  <input
                    type="url"
                    value={siteForm.instagramUrl}
                    onChange={(event) => setSiteField('instagramUrl', event.target.value)}
                  />
                </label>
                <label>
                  WhatsApp URL
                  <input
                    type="url"
                    value={siteForm.whatsappUrl}
                    onChange={(event) => setSiteField('whatsappUrl', event.target.value)}
                  />
                </label>

                <button type="submit" className="primary-btn">Save site settings</button>
              </form>
            </section>
          )}

          {activeTab === 'alumni' && (
            <section className="panel">
              <h2>Alumni content</h2>
              <p>Manage home banner text and banner images with optional heading for each banner.</p>
              <form className="panel-grid" onSubmit={handleSaveAlumniContent}>
                <label>
                  Alumni name
                  <input
                    type="text"
                    value={alumniForm.alumniName || ''}
                    onChange={(event) => setAlumniField('alumniName', event.target.value)}
                    placeholder="SFACO CTG"
                  />
                </label>
                <label className="field-span-full">
                  Alumni description
                  <textarea
                    rows={4}
                    value={alumniForm.alumniDescription || ''}
                    onChange={(event) => setAlumniField('alumniDescription', event.target.value)}
                    placeholder="Stay connected with classmates..."
                  />
                </label>
                <label className="field-span-full">
                  Banner heading
                  <input
                    type="text"
                    value={alumniForm.bannerHeading || ''}
                    onChange={(event) => setAlumniField('bannerHeading', event.target.value)}
                    placeholder="Reconnect with the alumni who shaped your story."
                  />
                </label>
                <div className="field-span-full alumni-banners-editor">
                  <div className="panel-header-row">
                    <h3>Banner list</h3>
                    <button type="button" onClick={addAlumniBanner}>Add banner</button>
                  </div>
                  {(Array.isArray(alumniForm.banners) ? alumniForm.banners : []).map((banner, index) => (
                    <div key={`alumni-banner-${index}`} className="sub-card banner-item-card">
                      <label className="field-span-full">
                        Upload banner image
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(event) => handleAlumniBannerUpload(index, event.target.files?.[0] || null)}
                        />
                        {bannerUploadingIndex === index && (
                          <span className="field-help">Uploading banner...</span>
                        )}
                      </label>
                      <label className="field-span-full">
                        Banner image URL
                        <input
                          type="url"
                          value={banner.imageUrl || ''}
                          onChange={(event) => setAlumniBannerField(index, 'imageUrl', event.target.value)}
                          placeholder="https://.../banner.jpg"
                        />
                      </label>
                      <label className="field-span-full">
                        Banner heading (optional)
                        <input
                          type="text"
                          value={banner.heading || ''}
                          onChange={(event) => setAlumniBannerField(index, 'heading', event.target.value)}
                          placeholder="Heading for this banner"
                        />
                      </label>
                      {banner.imageUrl && (
                        <div className="banner-preview-wrap">
                          <img src={banner.imageUrl} alt={`Banner ${index + 1}`} />
                        </div>
                      )}
                      <button type="button" onClick={() => removeAlumniBanner(index)}>Remove banner</button>
                    </div>
                  ))}
                </div>
                <button type="submit" className="primary-btn">Save alumni content</button>
              </form>
            </section>
          )}

          {activeTab === 'events' && (
            <section className="panel">
              <div className="panel-header-row">
                <div>
                  <h2>Events management</h2>
                  <p>All events are listed here. Add new events using the popup form.</p>
                </div>
                <button type="button" className="primary-btn" onClick={openAddEventModal}>
                  Add Event
                </button>
              </div>

              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Date</th>
                      <th>Action</th>
                      <th>Status</th>
                      <th>Sort</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eventsLoading ? (
                      <tr>
                        <td colSpan="6">Loading events...</td>
                      </tr>
                    ) : (
                      events.map((eventItem) => (
                        <tr key={eventItem.id}>
                          <td>{eventItem.title}</td>
                          <td>{eventItem.dateLabel || '-'}</td>
                          <td>{eventItem.action || '-'}</td>
                          <td>{eventItem.active ? 'Active' : 'Hidden'}</td>
                          <td>{eventItem.sortOrder}</td>
                          <td className="table-actions">
                            <button type="button" onClick={() => startEditEvent(eventItem)}>
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteEvent(eventItem)}
                              disabled={eventDeletingId === String(eventItem.id)}
                            >
                              {eventDeletingId === String(eventItem.id) ? 'Deleting...' : 'Delete'}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                    {!eventsLoading && !events.length && (
                      <tr>
                        <td colSpan="6">No events found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>
      </div>

      {profileModalOpen && (
        <div className="dashboard-modal" role="dialog" aria-modal="true" aria-labelledby="admin-profile-modal-title">
          <div className="dashboard-modal-backdrop" onClick={closeProfileModal} aria-hidden="true" />
          <div className="dashboard-modal-card">
            <div className="dashboard-modal-header">
              <div>
                <h2 id="admin-profile-modal-title">Edit member profile</h2>
                <p>{selectedProfile?.name || 'Loading member profile'}</p>
              </div>
              <button type="button" className="dashboard-modal-close" onClick={closeProfileModal} aria-label="Close profile editor">
                ×
              </button>
            </div>

            {profileLoading && <p>Loading member profile...</p>}
            {selectedProfile && !profileLoading && (
              <form className="panel-grid" onSubmit={handleSaveProfile}>
                {schema.map((field) => (
                  <label
                    key={field.name}
                    className={field.type === 'textarea' || field.type === 'file' ? 'field-span-full' : ''}
                  >
                    {field.label}
                    {renderField(field, selectedProfile?.[field.name], handleProfileFieldChange)}
                    {field.helperText && <span className="field-help">{field.helperText}</span>}
                  </label>
                ))}

                {selectedProfile.profile_photo_url && (
                  <div className="profile-photo-preview field-span-full">
                    <img src={selectedProfile.profile_photo_url} alt={selectedProfile.name || 'Profile'} />
                  </div>
                )}

                <div className="dashboard-modal-actions field-span-full">
                  <button type="button" onClick={closeProfileModal}>
                    Cancel
                  </button>
                  <button type="submit" className="primary-btn" disabled={profileSaving}>
                    {profileSaving ? 'Saving...' : 'Save member profile'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {eventModalOpen && (
        <div className="dashboard-modal" role="dialog" aria-modal="true" aria-labelledby="admin-event-modal-title">
          <div className="dashboard-modal-backdrop" onClick={closeEventModal} aria-hidden="true" />
          <div className="dashboard-modal-card">
            <div className="dashboard-modal-header">
              <div>
                <h2 id="admin-event-modal-title">{editingEventId ? 'Edit event' : 'Add event'}</h2>
                <p>{editingEventId ? 'Update this event details.' : 'Create a new event for the homepage carousel.'}</p>
              </div>
              <button
                type="button"
                className="dashboard-modal-close"
                onClick={closeEventModal}
                aria-label="Close event editor"
              >
                ×
              </button>
            </div>

            <form className="panel-grid" onSubmit={handleSaveEvent}>
              <label>
                Event title
                <input
                  type="text"
                  value={eventForm.title}
                  onChange={(event) => setEventField('title', event.target.value)}
                  required
                />
              </label>
              <label>
                Date label
                <input
                  type="text"
                  value={eventForm.dateLabel}
                  onChange={(event) => setEventField('dateLabel', event.target.value)}
                  placeholder="Jan 18-20"
                />
              </label>
              <label className="field-span-full">
                Description
                <textarea
                  rows={3}
                  value={eventForm.description}
                  onChange={(event) => setEventField('description', event.target.value)}
                  placeholder="Panels, campus tour, and class dinners."
                />
              </label>
              <label>
                Action text
                <input
                  type="text"
                  value={eventForm.action}
                  onChange={(event) => setEventField('action', event.target.value)}
                  placeholder="Reserve seat"
                />
              </label>
              <label>
                Action URL
                <input
                  type="url"
                  value={eventForm.actionUrl}
                  onChange={(event) => setEventField('actionUrl', event.target.value)}
                  placeholder="https://..."
                />
              </label>
              <label>
                Sort order
                <input
                  type="number"
                  value={eventForm.sortOrder}
                  onChange={(event) => setEventField('sortOrder', Number(event.target.value || 0))}
                />
              </label>
              <label className="inline-check">
                <input
                  type="checkbox"
                  checked={eventForm.active}
                  onChange={(event) => setEventField('active', event.target.checked)}
                />
                Visible on homepage
              </label>
              <div className="field-span-full event-form-actions">
                <button type="button" onClick={closeEventModal}>
                  Cancel
                </button>
                <button type="submit" className="primary-btn" disabled={eventSaving}>
                  {eventSaving ? 'Saving...' : editingEventId ? 'Update event' : 'Create event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
